'use client';

import Image from 'next/image';
import { useCallback, useState } from 'react';
import { Countdown } from './Countdown';
import { PasswordForm } from './PasswordForm';
import type { ComingSoonProps } from './types';

/**
 * Core Coming Soon Component
 * 
 * A sophisticated landing page for sites under construction.
 * Features:
 * - Optional countdown timer
 * - Optional contact information
 * - Optional social links
 * - Optional newsletter signup
 * - Password protection for bypassing
 * 
 * This is the core implementation. Theme-specific versions can extend
 * or wrap this component with custom styling.
 */
export function ComingSoon({
  brandName,
  logo,
  heading = 'Coming Soon',
  message = "We're working hard to bring you something amazing. Stay tuned!",
  launchDate,
  showCountdown = true,
  contactEmail,
  contactPhone,
  showContactInfo = false,
  socialLinks,
  showSocialLinks = false,
  showNewsletter = false,
  onNewsletterSubmit,
  backgroundImage,
  hasPasswordProtection = false,
  organisationId,
  locale,
  messages,
  tokens = {},
}: ComingSoonProps) {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handlePasswordSuccess = useCallback(() => {
    setIsUnlocked(true);
    // Set a cookie to remember the bypass
    document.cookie = `rr_coming_soon_bypass_${organisationId}=true; path=/; max-age=86400; SameSite=Lax`;
    // Reload to show the actual site
    window.location.reload();
  }, [organisationId]);

  const handleNewsletterSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!onNewsletterSubmit || !newsletterEmail) return;

      setNewsletterStatus('loading');
      try {
        await onNewsletterSubmit(newsletterEmail);
        setNewsletterStatus('success');
        setNewsletterEmail('');
      } catch {
        setNewsletterStatus('error');
      }
    },
    [newsletterEmail, onNewsletterSubmit]
  );

  // If unlocked, this component shouldn't render (page will reload)
  if (isUnlocked) {
    return null;
  }

  // Get theme colors from tokens
  const primaryColor = tokens['color-primary'] || tokens['--color-primary'] || '#1a365d';
  const accentColor = tokens['color-accent'] || tokens['--color-accent'] || '#BBA339';

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center px-4 py-12 overflow-hidden">
      {/* Background */}
      {backgroundImage ? (
        <>
          <Image
            src={backgroundImage}
            alt=""
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-black/50" />
        </>
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, #000 100%)`,
          }}
        />
      )}

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-1/2 -right-1/2 w-full h-full rounded-full opacity-10"
          style={{ background: `radial-gradient(circle, ${accentColor} 0%, transparent 70%)` }}
        />
        <div
          className="absolute -bottom-1/2 -left-1/2 w-full h-full rounded-full opacity-10"
          style={{ background: `radial-gradient(circle, ${primaryColor} 0%, transparent 70%)` }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-2xl mx-auto">
        {/* Logo */}
        {logo && (
          <div className="mb-8">
            <Image
              src={logo}
              alt={brandName}
              width={180}
              height={60}
              className="mx-auto h-12 md:h-16 w-auto object-contain"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
        )}

        {/* Brand Name (if no logo) */}
        {!logo && (
          <h2 className="text-2xl md:text-3xl font-bold text-white/90 mb-8 tracking-wide">
            {brandName}
          </h2>
        )}

        {/* Heading */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight">
          {heading}
        </h1>

        {/* Message */}
        <p className="text-lg md:text-xl text-white/80 mb-12 max-w-lg mx-auto leading-relaxed">
          {message}
        </p>

        {/* Countdown */}
        {showCountdown && launchDate && (
          <div className="mb-12">
            <Countdown
              targetDate={launchDate}
              className="max-w-md mx-auto"
              valueClassName="text-white"
              labelClassName="text-white/60"
            />
          </div>
        )}

        {/* Newsletter Signup */}
        {showNewsletter && (
          <div className="mb-12 max-w-md mx-auto">
            <h3 className="text-lg font-medium text-white mb-4">
              Get notified when we launch
            </h3>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              <button
                type="submit"
                disabled={newsletterStatus === 'loading'}
                className="px-6 py-3 bg-white text-gray-900 font-semibold rounded-lg hover:bg-white/90 transition-colors disabled:opacity-50"
              >
                {newsletterStatus === 'loading' ? '...' : 'Notify Me'}
              </button>
            </form>
            {newsletterStatus === 'success' && (
              <p className="text-green-400 text-sm mt-2">Thank you! We&apos;ll notify you when we launch.</p>
            )}
            {newsletterStatus === 'error' && (
              <p className="text-red-400 text-sm mt-2">Something went wrong. Please try again.</p>
            )}
          </div>
        )}

        {/* Contact Info */}
        {showContactInfo && (contactEmail || contactPhone) && (
          <div className="mb-12 space-y-2">
            <h3 className="text-lg font-medium text-white mb-4">Contact Us</h3>
            {contactEmail && (
              <a
                href={`mailto:${contactEmail}`}
                className="block text-white/80 hover:text-white transition-colors"
              >
                {contactEmail}
              </a>
            )}
            {contactPhone && (
              <a
                href={`tel:${contactPhone}`}
                className="block text-white/80 hover:text-white transition-colors"
              >
                {contactPhone}
              </a>
            )}
          </div>
        )}

        {/* Social Links */}
        {showSocialLinks && socialLinks && Object.keys(socialLinks).length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-center gap-4">
              {socialLinks.facebook && (
                <SocialLink href={socialLinks.facebook} label="Facebook">
                  <FacebookIcon />
                </SocialLink>
              )}
              {socialLinks.instagram && (
                <SocialLink href={socialLinks.instagram} label="Instagram">
                  <InstagramIcon />
                </SocialLink>
              )}
              {socialLinks.linkedin && (
                <SocialLink href={socialLinks.linkedin} label="LinkedIn">
                  <LinkedInIcon />
                </SocialLink>
              )}
              {socialLinks.twitter && (
                <SocialLink href={socialLinks.twitter} label="Twitter">
                  <TwitterIcon />
                </SocialLink>
              )}
              {socialLinks.youtube && (
                <SocialLink href={socialLinks.youtube} label="YouTube">
                  <YouTubeIcon />
                </SocialLink>
              )}
            </div>
          </div>
        )}

        {/* Password Protection */}
        {hasPasswordProtection && organisationId && (
          <div className="mt-8">
            {showPasswordForm ? (
              <div className="space-y-4">
                <PasswordForm
                  organisationId={organisationId}
                  onSuccess={handlePasswordSuccess}
                  locale={locale}
                  messages={messages}
                />
                <button
                  onClick={() => setShowPasswordForm(false)}
                  className="text-white/60 text-sm hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="text-white/60 text-sm hover:text-white transition-colors underline underline-offset-4"
              >
                Have access password?
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center">
        <p className="text-white/40 text-sm">
          © {new Date().getFullYear()} {brandName}. All rights reserved.
        </p>
      </div>
    </div>
  );
}

// Social Link Component
function SocialLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all"
    >
      {children}
    </a>
  );
}

// Social Icons
function FacebookIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function TwitterIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
